import { CardActions } from '@mui/material'
import { isEmpty } from 'ramda'
import React from 'react'
import { useSelector } from 'react-redux'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { selectOrganizationTypes } from '@/app/module/user/store/selectors'
import { selectCountries, selectDefaultCountryCode } from '@/app/module/utils/store/selectors'

import {
  getInvitationProfileDefaultValues,
  getInvitationProfileFields,
  getProfileDefaultValues,
  getProfileFields,
  InvitationProfileFormValues,
  invitationProfileSchema,
  ProfileFormValues,
  signupProfileSchema,
} from './specs'

type SignupProps = {
  mode: 'signup'
  data: Partial<ProfileFormValues>
  onSubmit: (data: ProfileFormValues) => void
}

type InvitationProps = {
  mode: 'invitation'
  data: Partial<InvitationProfileFormValues>
  onSubmit: (data: InvitationProfileFormValues) => void
}

type Props = SignupProps | InvitationProps

const ProfileForm: React.FC<Props> = ({ data, mode, onSubmit }) => {
  const isInvitationMode = mode === 'invitation'

  if (isInvitationMode) {
    return <InvitationProfile data={data} onSubmit={onSubmit} />
  }

  return <SignupProfile data={data} onSubmit={onSubmit} />
}

const SignupProfile: React.FC<Omit<SignupProps, 'mode'>> = ({ data, onSubmit }) => {
  const { countryOptions, defaultCountry } = useCountriesData()
  const organizationOptions = useSelector(selectOrganizationTypes)

  const defaultValues = React.useMemo(
    () => (!isEmpty(data) ? data : getProfileDefaultValues(defaultCountry, organizationOptions)),
    [data, defaultCountry, organizationOptions],
  )

  const fields = React.useMemo(
    () => getProfileFields(countryOptions, organizationOptions),
    [countryOptions, organizationOptions],
  )

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      schema={signupProfileSchema}
      shouldReset
      onSubmit={onSubmit}
      renderActions={({ isDirty, isValid }) => (
        <CardActions sx={{ marginTop: 2, padding: 0 }}>
          {/** So it won't disable button after going back */}
          <SubmitButton isDirty={isValid ? true : isDirty} isSubmitting={false}>
            Next
          </SubmitButton>
        </CardActions>
      )}
    />
  )
}

const InvitationProfile: React.FC<Omit<InvitationProps, 'mode'>> = ({ data, onSubmit }) => {
  const { countryOptions, defaultCountry } = useCountriesData()

  const defaultValues = React.useMemo(
    () => (!isEmpty(data) ? data : getInvitationProfileDefaultValues(defaultCountry)),
    [data, defaultCountry],
  )

  const fields = React.useMemo(() => getInvitationProfileFields(countryOptions), [countryOptions])

  return (
    <Form
      defaultValues={defaultValues}
      fields={fields}
      schema={invitationProfileSchema}
      shouldReset
      onSubmit={onSubmit}
      renderActions={({ isDirty, isValid }) => (
        <CardActions sx={{ marginTop: 2, padding: 0 }}>
          {/** So it won't disable button after going back */}
          <SubmitButton isDirty={isValid ? true : isDirty} isSubmitting={false}>
            Next
          </SubmitButton>
        </CardActions>
      )}
    />
  )
}

const useCountriesData = () => {
  const countries = useSelector(selectCountries)
  const defaultCountryCode = useSelector(selectDefaultCountryCode)
  const countryOptions = React.useMemo(
    () => countries.map((country) => ({ label: country.label, value: country.label })),
    [countries],
  )
  const defaultCountry = React.useMemo(
    () => countries.find((country) => country.value === defaultCountryCode)?.label || '',
    [countries, defaultCountryCode],
  )
  return { countryOptions, defaultCountry }
}

export default ProfileForm
